/**
 * User Quota Manager
 *
 * Manages per-user API quotas with different limits for each model tier.
 * Ensures fair resource distribution and prevents abuse while protecting
 * users who genuinely need crisis intervention.
 */

export class UserQuotaManager {
  constructor() {
    // Per-user daily quotas
    this.quotas = {
      opus: {
        daily: 3,
        monthly: 20,
        name: 'Critical Crisis',
        resetHours: 24
      },
      sonnet: {
        daily: 15,
        monthly: 200,
        name: 'Crisis & Complex',
        resetHours: 24
      },
      haiku: {
        daily: null, // Unlimited
        monthly: null,
        name: 'Routine',
        resetHours: null
      }
    };

    // Grace period for genuine emergencies
    this.emergencyOverride = {
      enabled: true,
      conditions: {
        overdueCount: 20,
        criticalOverdue: 8,
        hoursUntilDeadline: 12
      }
    };

    // Storage key prefix
    this.storagePrefix = 'user_quota_';
  }

  /**
   * Check if user can use specific model
   * @param {String} userId - User identifier
   * @param {String} model - Model tier (opus/sonnet/haiku)
   * @param {Object} context - User context for emergency override
   * @returns {Object} Quota check result
   */
  async checkQuota(userId, model, context = {}) {
    // Haiku is always available
    if (model === 'haiku') {
      return {
        allowed: true,
        reason: 'unlimited_tier'
      };
    }

    const usage = await this.getUserUsage(userId, model);
    const quota = this.quotas[model];

    // Check daily limit
    if (quota.daily && usage.daily >= quota.daily) {
      // Check for emergency override
      if (this.shouldOverrideForEmergency(context)) {
        return {
          allowed: true,
          reason: 'emergency_override',
          warning: 'Quota exceeded but emergency detected'
        };
      }

      // Suggest downgrade
      return {
        allowed: false,
        reason: 'daily_quota_exceeded',
        usage: usage.daily,
        limit: quota.daily,
        resetIn: this.getResetTime(userId, model),
        suggestion: this.getSuggestion(model, context)
      };
    }

    // Check monthly limit
    if (quota.monthly && usage.monthly >= quota.monthly) {
      return {
        allowed: false,
        reason: 'monthly_quota_exceeded',
        usage: usage.monthly,
        limit: quota.monthly,
        resetDate: this.getMonthlyResetDate(),
        suggestion: this.getSuggestion(model, context)
      };
    }

    // Allowed with remaining quota info
    return {
      allowed: true,
      reason: 'within_quota',
      remaining: {
        daily: quota.daily ? quota.daily - usage.daily : 'unlimited',
        monthly: quota.monthly ? quota.monthly - usage.monthly : 'unlimited'
      }
    };
  }

  /**
   * Record API usage for ANY type of request
   * @param {String} userId - User identifier
   * @param {String} model - Model used (opus/sonnet/haiku)
   * @param {Object} metadata - Additional usage metadata
   */
  async recordUsage(userId, model, metadata = {}) {
    const key = `${this.storagePrefix}${userId}`;
    const now = new Date();

    // Get existing usage data
    let userData = await this.getStoredData(key) || {
      opus: { daily: [], monthly: [] },
      sonnet: { daily: [], monthly: [] },
      haiku: { daily: [], monthly: [] }
    };

    // Enhanced usage record with request type tracking
    const usageRecord = {
      timestamp: now.toISOString(),
      model,
      requestType: metadata.type || 'unknown',
      category: this.categorizeRequest(metadata.type),
      ...metadata
    };

    // Track usage by category for analytics
    if (!userData.byCategory) {
      userData.byCategory = {};
    }
    const category = usageRecord.category;
    if (!userData.byCategory[category]) {
      userData.byCategory[category] = { daily: 0, monthly: 0 };
    }
    userData.byCategory[category].daily++;
    userData.byCategory[category].monthly++;

    // Update daily and monthly arrays
    if (!userData[model]) {
      userData[model] = { daily: [], monthly: [] };
    }

    userData[model].daily.push(usageRecord);
    userData[model].monthly.push(usageRecord);

    // Clean old entries
    userData = this.cleanOldEntries(userData);

    // Store updated data
    await this.storeData(key, userData);

    // Log for analytics
    console.log(`[Quota] Recorded ${model} usage for user ${userId}:`, {
      dailyUsage: userData[model].daily.length,
      monthlyUsage: userData[model].monthly.length,
      metadata
    });

    return {
      model,
      dailyUsage: userData[model].daily.length,
      monthlyUsage: userData[model].monthly.length
    };
  }

  /**
   * Get user's current usage
   */
  async getUserUsage(userId, model) {
    const key = `${this.storagePrefix}${userId}`;
    const userData = await this.getStoredData(key);

    if (!userData || !userData[model]) {
      return { daily: 0, monthly: 0 };
    }

    // Clean old entries first
    const cleaned = this.cleanOldEntries({ [model]: userData[model] });

    return {
      daily: cleaned[model].daily.length,
      monthly: cleaned[model].monthly.length
    };
  }

  /**
   * Clean entries older than quota period
   */
  cleanOldEntries(userData) {
    const now = new Date();
    const oneDayAgo = new Date(now - 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now);
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    for (const model of ['opus', 'sonnet', 'haiku']) {
      if (!userData[model]) continue;

      // Filter daily entries
      userData[model].daily = userData[model].daily.filter(entry => {
        const entryDate = new Date(entry.timestamp);
        return entryDate > oneDayAgo;
      });

      // Filter monthly entries
      userData[model].monthly = userData[model].monthly.filter(entry => {
        const entryDate = new Date(entry.timestamp);
        return entryDate > oneMonthAgo;
      });
    }

    return userData;
  }

  /**
   * Check if emergency override should apply
   */
  shouldOverrideForEmergency(context) {
    if (!this.emergencyOverride.enabled) return false;
    if (!context) return false;

    const conditions = this.emergencyOverride.conditions;

    // Check various emergency conditions
    if (context.overdueCount >= conditions.overdueCount) {
      console.log('[Quota] Emergency override: Too many overdue tasks');
      return true;
    }

    if (context.criticalOverdue >= conditions.criticalOverdue) {
      console.log('[Quota] Emergency override: Critical tasks overdue');
      return true;
    }

    if (context.hoursUntilDeadline <= conditions.hoursUntilDeadline) {
      console.log('[Quota] Emergency override: Imminent deadline');
      return true;
    }

    return false;
  }

  /**
   * Get model downgrade suggestion
   */
  getSuggestion(model, context) {
    if (model === 'opus') {
      return {
        fallbackModel: 'sonnet',
        message: 'Daily Opus limit reached. Using Sonnet for crisis support.',
        quality: 'Still excellent for crisis recovery'
      };
    }

    if (model === 'sonnet') {
      // Check if situation is severe enough to warrant waiting
      if (context?.overdueCount > 10) {
        return {
          fallbackModel: 'wait',
          message: 'Daily Sonnet limit reached. Given your situation, consider waiting for reset or seek human help.',
          resetIn: this.getResetTime(context.userId, 'sonnet')
        };
      }

      return {
        fallbackModel: 'haiku',
        message: 'Daily Sonnet limit reached. Using Haiku for basic optimization.',
        quality: 'Good for simple scheduling adjustments'
      };
    }

    return null;
  }

  /**
   * Get time until daily reset
   */
  getResetTime(userId, model) {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const hoursUntilReset = Math.ceil((tomorrow - now) / (1000 * 60 * 60));

    return {
      hours: hoursUntilReset,
      resetAt: tomorrow.toISOString(),
      display: `${hoursUntilReset} hours`
    };
  }

  /**
   * Get monthly reset date
   */
  getMonthlyResetDate() {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    return {
      date: nextMonth.toISOString(),
      display: nextMonth.toLocaleDateString()
    };
  }

  /**
   * Categorize request type for analytics
   */
  categorizeRequest(type) {
    const categories = {
      // Crisis & Recovery
      'crisis_recovery': 'crisis',
      'workload_optimization': 'crisis',
      'emergency_intervention': 'crisis',

      // Essay & Writing
      'essay_review': 'essay',
      'essay_strategy': 'essay',
      'essay_brainstorm': 'essay',
      'essay_feedback': 'essay',
      'writing_improvement': 'essay',

      // Strategic Planning
      'college_strategy': 'strategy',
      'strategic_assessment': 'strategy',
      'timeline_planning': 'strategy',
      'application_strategy': 'strategy',

      // Academic
      'test_prep': 'academic',
      'course_planning': 'academic',
      'grade_improvement': 'academic',

      // General Help
      'general_advice': 'general',
      'question_answer': 'general',
      'task_batching': 'general',
      'scheduling': 'general'
    };

    return categories[type] || 'other';
  }

  /**
   * Get comprehensive usage analytics
   */
  async getUsageAnalytics(userId = null) {
    if (userId) {
      // Single user analytics
      const userData = await this.getStoredData(`${this.storagePrefix}${userId}`);
      if (!userData) return null;

      return {
        userId,
        models: {
          opus: {
            daily: userData.opus?.daily.length || 0,
            monthly: userData.opus?.monthly.length || 0
          },
          sonnet: {
            daily: userData.sonnet?.daily.length || 0,
            monthly: userData.sonnet?.monthly.length || 0
          },
          haiku: {
            daily: userData.haiku?.daily.length || 0,
            monthly: userData.haiku?.monthly.length || 0
          }
        },
        byCategory: userData.byCategory || {},
        recentRequests: this.getRecentRequests(userData)
      };
    } else {
      // System-wide analytics (admin view)
      return await this.getSystemWideAnalytics();
    }
  }

  /**
   * Get system-wide usage analytics for admin dashboard
   */
  async getSystemWideAnalytics() {
    const allUserKeys = await this.getAllUserKeys();
    const analytics = {
      totalUsers: 0,
      totalRequests: {
        opus: { daily: 0, monthly: 0 },
        sonnet: { daily: 0, monthly: 0 },
        haiku: { daily: 0, monthly: 0 }
      },
      byCategory: {
        crisis: { daily: 0, monthly: 0 },
        essay: { daily: 0, monthly: 0 },
        strategy: { daily: 0, monthly: 0 },
        academic: { daily: 0, monthly: 0 },
        general: { daily: 0, monthly: 0 },
        other: { daily: 0, monthly: 0 }
      },
      quotaExhausted: {
        opus: 0,
        sonnet: 0
      },
      emergencyOverrides: 0,
      topUsers: [],
      hourlyCost: 0,
      dailyCost: 0,
      projectedMonthlyCost: 0
    };

    for (const key of allUserKeys) {
      const userData = await this.getStoredData(key);
      if (!userData) continue;

      analytics.totalUsers++;

      // Aggregate model usage
      for (const model of ['opus', 'sonnet', 'haiku']) {
        if (userData[model]) {
          analytics.totalRequests[model].daily += userData[model].daily.length;
          analytics.totalRequests[model].monthly += userData[model].monthly.length;
        }
      }

      // Aggregate category usage
      if (userData.byCategory) {
        for (const [category, counts] of Object.entries(userData.byCategory)) {
          if (analytics.byCategory[category]) {
            analytics.byCategory[category].daily += counts.daily || 0;
            analytics.byCategory[category].monthly += counts.monthly || 0;
          }
        }
      }

      // Check quota exhaustion
      const quotaStatus = await this.checkQuota(
        key.replace(this.storagePrefix, ''),
        'opus'
      );
      if (!quotaStatus.allowed) {
        analytics.quotaExhausted.opus++;
      }
    }

    // Calculate costs
    analytics.hourlyCost = this.calculateCost(analytics.totalRequests, 'hourly');
    analytics.dailyCost = this.calculateCost(analytics.totalRequests, 'daily');
    analytics.projectedMonthlyCost = analytics.dailyCost * 30;

    return analytics;
  }

  /**
   * Calculate API costs
   */
  calculateCost(usage, period = 'daily') {
    const costs = {
      opus: 0.105,   // Per request
      sonnet: 0.021,
      haiku: 0.002
    };

    let total = 0;
    for (const [model, data] of Object.entries(usage)) {
      const count = period === 'hourly' ? data.daily / 24 : data.daily;
      total += count * (costs[model] || 0);
    }

    return Math.round(total * 100) / 100; // Round to cents
  }

  /**
   * Get recent requests for analysis
   */
  getRecentRequests(userData, limit = 10) {
    const allRequests = [];

    for (const model of ['opus', 'sonnet', 'haiku']) {
      if (userData[model]?.daily) {
        allRequests.push(...userData[model].daily.map(r => ({
          ...r,
          model
        })));
      }
    }

    // Sort by timestamp and return most recent
    return allRequests
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, limit);
  }

  /**
   * Get user's quota status for all models
   */
  async getQuotaStatus(userId) {
    const status = {};

    for (const model of ['opus', 'sonnet', 'haiku']) {
      const usage = await this.getUserUsage(userId, model);
      const quota = this.quotas[model];

      status[model] = {
        name: quota.name,
        daily: {
          used: usage.daily,
          limit: quota.daily || 'unlimited',
          remaining: quota.daily ? Math.max(0, quota.daily - usage.daily) : 'unlimited'
        },
        monthly: {
          used: usage.monthly,
          limit: quota.monthly || 'unlimited',
          remaining: quota.monthly ? Math.max(0, quota.monthly - usage.monthly) : 'unlimited'
        }
      };
    }

    return status;
  }

  /**
   * Reset user quotas (admin function)
   */
  async resetUserQuotas(userId, model = null) {
    const key = `${this.storagePrefix}${userId}`;

    if (model) {
      // Reset specific model
      const userData = await this.getStoredData(key) || {};
      if (userData[model]) {
        userData[model] = { daily: [], monthly: [] };
        await this.storeData(key, userData);
      }
    } else {
      // Reset all models
      await this.storeData(key, {
        opus: { daily: [], monthly: [] },
        sonnet: { daily: [], monthly: [] },
        haiku: { daily: [], monthly: [] }
      });
    }

    console.log(`[Quota] Reset quotas for user ${userId}`, model ? `(${model} only)` : '(all models)');
  }

  /**
   * Storage interface (can be replaced with database)
   */
  async getStoredData(key) {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('[Quota] Failed to get stored data:', error);
      return null;
    }
  }

  async storeData(key, data) {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('[Quota] Failed to store data:', error);
      return false;
    }
  }

  /**
   * Get all user keys for analytics
   */
  async getAllUserKeys() {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(this.storagePrefix) && !key.includes('custom_')) {
        keys.push(key);
      }
    }
    return keys;
  }

  /**
   * Configure custom quotas for specific users (e.g., premium)
   */
  setUserCustomQuota(userId, customQuotas) {
    const key = `${this.storagePrefix}custom_${userId}`;
    localStorage.setItem(key, JSON.stringify(customQuotas));
  }

  /**
   * Get custom quotas if they exist
   */
  getUserCustomQuota(userId) {
    const key = `${this.storagePrefix}custom_${userId}`;
    const custom = localStorage.getItem(key);
    return custom ? JSON.parse(custom) : null;
  }
}